package top.wcpe.allincore.ui.web.asset

import top.wcpe.allincore.dsl.compiler.ast.ComponentAst
import top.wcpe.allincore.dsl.compiler.ast.ImportDecl
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.lex.Lexer
import top.wcpe.allincore.dsl.compiler.parse.Parser
import top.wcpe.allincore.dsl.compiler.source.SourceFile
import kotlin.js.Promise

internal enum class PreviewDependencyReferenceType {
    RELATIVE,
    ALIAS,
    BARE_NAME,
}

internal data class PreviewDependencyDeclaration(
    val kind: AssetReferenceKind,
    val referenceType: PreviewDependencyReferenceType,
    val reference: String,
)

internal fun interface PreviewDependencyDeclarationReader {
    fun read(source: SourceFile): List<PreviewDependencyDeclaration>
}

internal class DslCompilerPreviewDependencyDeclarationReader : PreviewDependencyDeclarationReader {
    override fun read(source: SourceFile): List<PreviewDependencyDeclaration> = try {
        when (val ast = Parser(Lexer(source.text, source.path).lex(), source.path).parseTopLevel()) {
            is ScreenAst -> importDeclarations(ast.imports) + ast.themeDeclaration()
            is ComponentAst -> importDeclarations(ast.imports)
            else -> emptyList()
        }
    } catch (error: Throwable) {
        throw AssetPolicyException("依赖声明解析失败：${source.path}")
    }

    private fun importDeclarations(imports: List<ImportDecl>): List<PreviewDependencyDeclaration> =
        imports.map { declaration ->
            if (declaration.path.isRelativeReference()) {
                PreviewDependencyDeclaration(
                    AssetReferenceKind.SOURCE_IMPORT,
                    PreviewDependencyReferenceType.RELATIVE,
                    declaration.path,
                )
            } else {
                PreviewDependencyDeclaration(
                    AssetReferenceKind.SOURCE_IMPORT,
                    PreviewDependencyReferenceType.ALIAS,
                    declaration.alias,
                )
            }
        }

    private fun ScreenAst.themeDeclaration(): List<PreviewDependencyDeclaration> {
        val scheme = (theme?.tokens?.get("scheme") as? String)?.takeIf { it.isNotBlank() } ?: return emptyList()
        return listOf(
            PreviewDependencyDeclaration(AssetReferenceKind.THEME, PreviewDependencyReferenceType.BARE_NAME, scheme),
        )
    }
}

internal class PreviewDependencyRegistry(
    aliases: Map<String, List<String>> = emptyMap(),
    bareNames: Map<String, List<String>> = emptyMap(),
) {
    private val aliasValues = aliases.copyRegistry()
    private val bareNameValues = bareNames.copyRegistry()

    fun resolveAlias(name: String): String = unique(name, aliasValues[name], "别名")

    fun resolveBareName(name: String): String = unique(name, bareNameValues[name], "裸名")

    /** 已登记裸名主题路径全集（供博物馆预览注入全部 scheme）。 */
    fun allBareNamePaths(): List<String> =
        bareNameValues.values.flatten().map(AssetPathPolicy::external).distinct().sorted()

    private fun unique(logicalName: String, candidates: List<String>?, category: String): String {
        val canonical = candidates.orEmpty().map(AssetPathPolicy::external).distinct().sorted()
        if (canonical.isEmpty()) failDependency("${category}未登记：$logicalName")
        if (canonical.size > 1) failDependency("$category 映射多义：$logicalName: ${canonical.joinToString()}")
        return canonical.single()
    }
}

internal class PreviewDependencyScanner(
    private val resolver: PreviewAssetResolver,
    private val declarationReader: PreviewDependencyDeclarationReader = DslCompilerPreviewDependencyDeclarationReader(),
    private val registry: PreviewDependencyRegistry = PreviewDependencyRegistry(),
    /**
     * true 时除 AST 声明的 scheme 外，再注入 registry 中全部 bareName 主题，
     * 使 local.themes() 返回完整目录（UI 博物馆 ThemePicker 需要）。
     */
    private val includeAllRegisteredThemes: Boolean = false,
) {
    fun scan(rootPath: String): Promise<AuthoritativeReadSet> = scanCandidate(rootPath).then(
        onFulfilled = { readSet ->
            resolver.publishCandidate()?.dispose()
            readSet
        },
        onRejected = { error ->
            resolver.discardCandidate()
            throw error
        },
    )

    internal fun scanCandidate(rootPath: String): Promise<AuthoritativeReadSet> {
        val rootRead = try {
            resolver.beginCandidate()
            resolver.resolveText(rootPath)
        } catch (error: Throwable) {
            resolver.discardCandidate()
            return Promise.reject(error)
        }
        return rootRead.flatMap { root ->
            val state = ScanState(root.toSourceAsset(PreviewReadKind.ROOT_SOURCE))
            visit(root, state).flatMap {
                if (!includeAllRegisteredThemes) {
                    Promise.resolve(state.snapshot())
                } else {
                    injectRegisteredThemes(root, state).then { state.snapshot() }
                }
            }
        }
    }

    /** 把 registry 中全部主题源并入依赖集（已访问过的路径跳过）。 */
    private fun injectRegisteredThemes(
        referrer: ResolvedAsset<String>,
        state: ScanState,
    ): Promise<Unit> {
        val paths = registry.allBareNamePaths()
        return injectThemePaths(referrer, paths, 0, state)
    }

    private fun injectThemePaths(
        referrer: ResolvedAsset<String>,
        paths: List<String>,
        index: Int,
        state: ScanState,
    ): Promise<Unit> {
        if (index >= paths.size) return Promise.resolve(Unit)
        val path = paths[index]
        if (state.isVisited(path)) {
            return injectThemePaths(referrer, paths, index + 1, state)
        }
        return resolver.resolveRegistered(referrer.fileProvenance, path, AssetReferenceKind.THEME).flatMap { asset ->
            state.addEdge(referrer.canonicalPath, asset, AssetReferenceKind.THEME)
            nextVisit(asset, AssetReferenceKind.THEME, state).flatMap {
                injectThemePaths(referrer, paths, index + 1, state)
            }
        }.catch {
            // 缺文件时不阻断主编译：跳过该主题
            injectThemePaths(referrer, paths, index + 1, state)
        }
    }

    private fun visit(asset: ResolvedAsset<String>, state: ScanState): Promise<Unit> {
        state.enter(asset.canonicalPath)
        val declarations = declarationReader.read(SourceFile(asset.canonicalPath, asset.content))
            .filter { it.kind.isRecursiveDependency() }
        return visitDeclarations(asset, declarations, 0, state).then {
            state.leave(asset.canonicalPath)
        }
    }

    private fun visitDeclarations(
        referrer: ResolvedAsset<String>,
        declarations: List<PreviewDependencyDeclaration>,
        index: Int,
        state: ScanState,
    ): Promise<Unit> {
        if (index == declarations.size) return Promise.resolve(Unit)
        val declaration = declarations[index]
        val dependency = try {
            resolve(referrer, declaration)
        } catch (error: Throwable) {
            return Promise.reject(error)
        }
        return dependency.flatMap { asset ->
            state.addEdge(referrer.canonicalPath, asset, declaration.kind)
            nextVisit(asset, declaration.kind, state).flatMap {
                visitDeclarations(referrer, declarations, index + 1, state)
            }
        }
    }

    private fun nextVisit(
        dependency: ResolvedAsset<ByteArray>,
        kind: AssetReferenceKind,
        state: ScanState,
    ): Promise<Unit> = when {
        state.isActive(dependency.canonicalPath) -> Promise.reject(
            AssetPolicyException(state.cycleDiagnostic(dependency.canonicalPath)),
        )
        state.isVisited(dependency.canonicalPath) -> Promise.resolve(Unit)
        else -> {
            state.addDependency(dependency, kind)
            if (kind == AssetReferenceKind.SOURCE_IMPORT) visit(dependency.toText(), state) else Promise.resolve(Unit)
        }
    }

    private fun resolve(
        referrer: ResolvedAsset<String>,
        declaration: PreviewDependencyDeclaration,
    ): Promise<ResolvedAsset<ByteArray>> = when (declaration.referenceType) {
        PreviewDependencyReferenceType.RELATIVE -> {
            requireRelative(declaration.reference)
            resolver.resolveRelative(referrer.fileProvenance, declaration.reference, declaration.kind)
        }
        PreviewDependencyReferenceType.ALIAS -> resolver.resolveRegistered(
            referrer.fileProvenance,
            registry.resolveAlias(declaration.reference),
            declaration.kind,
        )
        PreviewDependencyReferenceType.BARE_NAME -> resolver.resolveRegistered(
            referrer.fileProvenance,
            registry.resolveBareName(declaration.reference),
            declaration.kind,
        )
    }
}

private class ScanState(private val rootValue: AuthoritativeSourceAsset) {
    private val dependencies = linkedMapOf<String, AuthoritativeSourceAsset>()
    private val edges = mutableListOf<AuthoritativeDependencyEdge>()
    private val visited = mutableSetOf(rootValue.canonicalPath)
    private val active = mutableListOf<String>()

    fun enter(path: String) {
        active += path
    }

    fun leave(path: String) {
        check(active.removeLast() == path)
    }

    fun isActive(path: String): Boolean = path in active

    fun isVisited(path: String): Boolean = path in visited

    fun addDependency(asset: ResolvedAsset<ByteArray>, kind: AssetReferenceKind) {
        if (visited.add(asset.canonicalPath)) {
            dependencies[asset.canonicalPath] = asset.toSourceAsset(kind.toPreviewReadKind())
        }
    }

    fun addEdge(referrer: String, asset: ResolvedAsset<ByteArray>, kind: AssetReferenceKind) {
        edges += AuthoritativeDependencyEdge(referrer, asset.canonicalPath, kind.toPreviewReadKind())
    }

    fun cycleDiagnostic(path: String): String {
        val start = active.indexOf(path)
        return "依赖循环：${(active.drop(start) + path).joinToString(" -> ")}"
    }

    fun snapshot(): AuthoritativeReadSet = AuthoritativeReadSet(rootValue, dependencies.values.toList(), edges)
}

private fun ResolvedAsset<String>.toSourceAsset(kind: PreviewReadKind): AuthoritativeSourceAsset =
    AuthoritativeSourceAsset(canonicalPath, kind, contentVersion, actualBytes, content)

private fun ResolvedAsset<ByteArray>.toSourceAsset(kind: PreviewReadKind): AuthoritativeSourceAsset =
    AuthoritativeSourceAsset(canonicalPath, kind, contentVersion, actualBytes, content.decodeToString())

private fun ResolvedAsset<ByteArray>.toText(): ResolvedAsset<String> =
    ResolvedAsset(canonicalPath, actualBytes, mime, contentVersion, content.decodeToString(), fileProvenance)

private fun AssetReferenceKind.isRecursiveDependency(): Boolean =
    this == AssetReferenceKind.SOURCE_IMPORT || this == AssetReferenceKind.THEME

private fun String.isRelativeReference(): Boolean = startsWith("./") || startsWith("../")

private fun Map<String, List<String>>.copyRegistry(): Map<String, List<String>> =
    entries.associate { (name, paths) -> name to paths.toList() }

private fun requireRelative(reference: String) {
    if (!reference.startsWith("./") && !reference.startsWith("../")) {
        failDependency("相对依赖必须显式使用 ./ 或 ../")
    }
}

private fun <T, R> Promise<T>.flatMap(transform: (T) -> Promise<R>): Promise<R> =
    then(transform).unsafeCast<Promise<R>>()

private fun failDependency(message: String): Nothing = throw AssetPolicyException(message)
